/* eslint-disable no-mixed-spaces-and-tabs */
import { PencilIcon, TrashIcon } from '@heroicons/react/outline';
import { Button, Checkbox, IconButton } from '@mui/material';
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid-pro';
import { useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';
import { KeyedMutator } from 'swr';
import moment from 'moment';

import { NContact } from '@modules/ContactManager/components/Tabs/components/Contact/validation';
import { dataGridCol, gridCurrencyCell } from '@shared/components/grid/common';
import { PhoenixGrid } from '@shared/components/grid/PhoenixGrid';
import { LoaderBackdrop } from '@shared/components/LoaderBackdrop';
import { Roles } from '@shared/constants/constants';
import { useUserAccount } from '@shared/hooks/useUserAccount';
import { setLastCommentsRefresh } from '@store/reducers/contactManagerSlice';
import { GridWrapper } from '@styles/ContactManager/DealStyles';
import { colorTheme } from '@styles/muiTheme';

import { productTypeNames } from '@modules/ContactManager/components/Tabs/shared/DealCalculator/components/DealCalculatorLenders/components/LenderSection/LenderSectionProducts';
import { setIsDealStructureChanged } from '@store/reducers/dealSlice';
import { IDeal, IEditRetail, IProduct } from '../../interfaces';
import { ProductStatus, ProductType } from './constants';
import { EditRetailModal } from './EditRetailModal';
import { deleteProduct, requestUpdateCancellationRequest, voidProduct } from './queries';

const gridRowActionsIconProps = {
  color: colorTheme.palette.primary.light,
  width: 18,
};

interface IProductsTableProps {
  data: IProduct[];
  dealId: string;
  mutateDealProductsSummary: () => void;
  contactAddress?: NContact.Address;
  reloadDeal: KeyedMutator<IDeal>;
  isVoidEnable: boolean;
  gridLoading: boolean;
  pageLoading: boolean;
  preventDealSave: boolean;
}

export const ProductsTable = ({
  data,
  dealId,
  mutateDealProductsSummary,
  contactAddress,
  reloadDeal,
  isVoidEnable,
  gridLoading,
  pageLoading,
  preventDealSave,
}: IProductsTableProps) => {
  const dispatch = useDispatch();
  const { userHasRoles, isAdmin } = useUserAccount();
  const [editProduct, setEditProduct] = useState<Partial<IEditRetail> | null>(null);
  const [isEditRetailModalOpen, setIsEditRetailModalOpen] = useState(0);
  const [loading, setLoading] = useState(false);
  const hasCSRole = userHasRoles([Roles.CUSTOMER_SERVICE]);

  const isEditProductEnable = (isAdmin || userHasRoles([Roles.EDIT_PRODUCT_PRICE])) && !preventDealSave;

  const voidRetail = useCallback(
    async ({ dealProductId }: IProduct) => {
      setLoading(true);
      await voidProduct(dealProductId, dealId);
      setLoading(false);
      await mutateDealProductsSummary();
      await reloadDeal?.();
      dispatch(setIsDealStructureChanged({data: { isDealStructureChanged: true }, dealId}));
    },
    [dealId]
  );

  const renderCellActions = useCallback(
    (params: GridRenderCellParams) => {
      const { productTypeName, status: productStatus } = params.row;
      const isMbi = productTypeName?.toLowerCase() === productTypeNames.mbi.toLowerCase();
      const canEditProduct =
        isEditProductEnable &&
        !isMbi &&
        !(contactAddress?.state === 'FL' && (productTypeName === ProductType.COM || productTypeName === ProductType.VSC));

      const handleEditClick = () => {
        const { cost, markup, retail, dealId, dealProductId } = params.row;
        if (!editProduct) {
          setEditProduct({ cost, markup, retail, dealId, dealProductId, reason: '', productTypeName });
          setIsEditRetailModalOpen((prevState) => prevState + 1); 
        }
      };

      const handleTrashClick = useCallback(async () => {
        setLoading(true);
        await deleteProduct(params.row.dealProductId, dealId);
        mutateDealProductsSummary();
        dispatch(setLastCommentsRefresh(new Date().toISOString()));
        reloadDeal?.();
        setLoading(false);
      }, [params, dealId]);

      return (
        <>
          {(productStatus === ProductStatus.NOT_CONTRACTED || productStatus === ProductStatus.ERROR) && (
            <>
              {!preventDealSave ? (
                <IconButton onClick={handleTrashClick}>
                  <TrashIcon {...gridRowActionsIconProps} />
                </IconButton>
              ) : null}
              {canEditProduct ? (
                <IconButton onClick={handleEditClick}>
                  <PencilIcon {...gridRowActionsIconProps} />
                </IconButton>
              ) : null}
            </>
          )}

          {productStatus === ProductStatus.CONTRACTED && isVoidEnable && (
            <Button onClick={() => voidRetail(params.row)}>Void</Button>
          )}
        </>
      );
    },
    [contactAddress, dealId, isEditProductEnable]
  );

  const handleClose = useCallback(() => {
    setEditProduct(null);
    setIsEditRetailModalOpen(0);
  }, []);

  const handleChange = useCallback(
    async (checked: boolean, { dealProductId }: IProduct) => {
      const data = { cancelRequestDate: moment().toISOString(), isCancellationRequest: checked };
      setLoading(true);
      await requestUpdateCancellationRequest(data, dealId, dealProductId);
      dispatch(setLastCommentsRefresh(new Date().toISOString()));
      setLoading(false);
    },
    [dealId, dispatch, requestUpdateCancellationRequest]
  );

  const columns: GridColDef[] = [
    dataGridCol('status', {
      minWidth: 80,
      flex: 1,
    }),
    dataGridCol('productTypeName', {
      headerName: 'Product',
      minWidth: 100,
      flex: 1,
    }),
    dataGridCol('providerName', {
      headerName: 'Provider',
      minWidth: 100,
      flex: 1,
    }),
    dataGridCol('cost', {
      minWidth: 100,
      flex: 1.2,
      ...gridCurrencyCell,
    }),
    dataGridCol('markup', {
      minWidth: 100,
      flex: 1,
      ...gridCurrencyCell,
    }),
    dataGridCol('retail', {
      minWidth: 100,
      flex: 1,
      ...gridCurrencyCell,
    }),
    dataGridCol('plan', {
      minWidth: 100,
      flex: 2.5,
      textWrap: true,
    }),
    dataGridCol('details', {
      minWidth: 100,
      flex: 1.5,
      textWrap: true,
    }),
    dataGridCol('isCancellationRequest', {
      headerName: 'Cancellation Request',
      minWidth: 120,
      sortable: false,
      renderCell: ({ row }) => 
        <Checkbox 
        checked={row.isCancellationRequest} 
        disabled={!hasCSRole}
        onChange={(event) => handleChange(event.target.checked, row)}/>,
    }),
    {
      headerName: 'Action',
      flex: 1,
      minWidth: 80,
      field: 'options',
      sortable: false,
      disableColumnMenu: true,
      renderCell: renderCellActions,
    },
  ].map((i) => ({ minWidth: 50, flex: 0.5, ...i }));

  return (
    <>
      <GridWrapper>
        <PhoenixGrid
          autoHeight
          hideFooter
          canNotCopy
          isFlexible
          autoRowHeight
          disableSelectionOnClick
          columns={columns}
          loading={gridLoading}
          rows={gridLoading ? [] : data}
          getRowId={(row) => row.dealProductId}
        />
      </GridWrapper>
      <LoaderBackdrop open={loading || pageLoading} />

      {editProduct && (
        <EditRetailModal
          isEditRetailModalOpen={isEditRetailModalOpen}
          editProduct={editProduct}
          dealId={dealId}
          closeModal={handleClose}
          reloadDeal={reloadDeal}
          contactState={contactAddress?.state}
        />
      )}
    </>
  );
};
